import React, { useEffect, useRef } from 'react';

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  className?: string;
  autoResize?: boolean;
  minRows?: number;
  maxRows?: number;
}

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className = '', autoResize = false, minRows = 1, maxRows = 6, ...props }, ref) => {
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = (ref as React.RefObject<HTMLTextAreaElement>) || internalRef;

    // Use text-base (16px) on mobile to prevent iOS Safari auto-zoom on focus,
    // then text-sm (14px) on sm+ where zoom isn't triggered.
    const baseStyles = 'flex min-h-[40px] w-full rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none';
    
    const classes = [baseStyles, className].filter(Boolean).join(' ');

    // Auto-resize functionality
    useEffect(() => {
      if (!autoResize || !textareaRef.current) return;

      const textarea = textareaRef.current;
      const resizeTextArea = () => {
        // Reset height to auto to get correct scrollHeight
        textarea.style.height = 'auto';
        
        // Calculate the number of lines
        const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight, 10) || 20;
        const lines = Math.ceil(textarea.scrollHeight / lineHeight);
        
        // Constrain between minRows and maxRows
        const constrainedLines = Math.max(minRows, Math.min(maxRows, lines));
        
        // Set the height based on constrained lines
        const newHeight = constrainedLines * lineHeight + (textarea.offsetHeight - textarea.clientHeight);
        textarea.style.height = `${newHeight}px`;
      };

      // Initial resize
      resizeTextArea();

      // Add event listener for input changes
      textarea.addEventListener('input', resizeTextArea);

      // Cleanup
      return () => {
        textarea.removeEventListener('input', resizeTextArea);
      };
    }, [autoResize, minRows, maxRows, props.value, textareaRef]);

    return (
      <textarea
        className={classes}
        ref={textareaRef}
        rows={minRows}
        autoComplete="off"
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore="true"
        {...props}
      />
    );
  }
);

TextArea.displayName = 'TextArea';

export { TextArea };
export type { TextAreaProps };
